import React, { FC } from 'react';
import styled from '@emotion/styled';
import { ICalculatedBasket, IShippingType } from '$models';
import { useCheckoutBasket, useTranslation } from '$hooks';
import { formatPrice } from '$lib/helpers';
import { Flex, Grid } from '$components/layouts';
import { B2CDeliveryMethod } from '$constants';
import { Button } from '$components/elements/button';

type BasketOrderSummmaryProps = {
    basket?: ICalculatedBasket;
    shipping?: IShippingType;
    isCheckout?: boolean;
};

export const BasketOrderSummary: FC<BasketOrderSummmaryProps> = ({
    basket,
    shipping,
    isCheckout,
}: BasketOrderSummmaryProps): JSX.Element => {
    const { translate } = useTranslation();
    const { clearVoucher } = useCheckoutBasket();

    const freeShipping = shipping?.shippingPriceLimit
        ? !(shipping?.shippingPriceLimit - (basket?.totalIncVAT || 0))
        : false;

    const totalShippingCost = calculateTotalShippingCost(basket, shipping, freeShipping);

    const shouldDisplayDefaultShipping = calculateShouldDisplayDefaultShipping(basket);

    return (
        <>
            <TopSummary flow="row" rowGap="extrasmall" columns="auto auto">
                {/* Total */}
                <div>{translate('checkout.orderSummary.subTotal')}</div>
                <Price>{formatPrice(basket?.totalExDiscount)}</Price>
                {/* Shipping */}
                {!basket?.discount && !!basket?.lines && shipping && (
                    <>
                        {!basket?.shippingKey && !shipping?.downloadOnly ? (
                            <div>{translate('checkout.orderSummary.defaultDeliveryLabel')}</div>
                        ) : (
                            <>
                                {!isCheckout && shouldDisplayDefaultShipping ? (
                                    <div>{translate('checkout.orderSummary.defaultDeliveryLabel')}</div>
                                ) : (
                                    <div>
                                        {shipping?.name
                                            ? `${translate('checkout.orderSummary.deliveryPrefixLabel')} (${
                                                  shipping.name
                                              })`
                                            : translate('checkout.orderSummary.deliveryPrefixLabel')}
                                    </div>
                                )}
                            </>
                        )}
                        <Price>{totalShippingCost}</Price>
                    </>
                )}
                {/* Voucher shipping */}
                {!basket?.discount && (basket?.shippingDiscount || 0) > 0 && !!basket?.vouchers && !!basket?.lines && (
                    <>
                        <Discount>
                            <Discounts>
                                <DiscountLabel>{` - ${translate(
                                    'checkout.orderSummary.voucherLabel'
                                )}: `}</DiscountLabel>
                            </Discounts>
                        </Discount>
                        <Price discount>{freeShipping ? formatPrice(0) : formatPrice(basket.shippingDiscount)}</Price>
                    </>
                )}
                {/* Voucher */}
                {(basket?.voucherDiscount || 0) > 0 && basket?.vouchers && (
                    <>
                        <Discount>
                            <Discounts>
                                <DiscountLabel>
                                    {` - ${translate('checkout.orderSummary.voucherLabel')}: `}
                                </DiscountLabel>
                            </Discounts>
                        </Discount>
                        <Price discount>{formatPrice(basket.voucherDiscount)}</Price>
                    </>
                )}
                {/* Normal discount */}
                {!!basket?.discount && basket.discount - (basket.voucherDiscount || 0) > 0 && (
                    <>
                        <Discount>{translate('checkout.orderSummary.discountLabel')}</Discount>
                        <Price discount>{formatPrice(basket.discount - (basket.voucherDiscount || 0))}</Price>
                    </>
                )}
                {!!basket?.vouchers && !!basket.discount && (
                    <>
                        <div>
                            {shipping?.name
                                ? `${translate('checkout.orderSummary.deliveryPrefixLabel')} (${shipping.name})`
                                : translate('checkout.orderSummary.deliveryPrefixLabel')}
                        </div>
                        <Price>{totalShippingCost}</Price>
                    </>
                )}
                {/* Voucher discount shipping */}
                {(basket?.shippingDiscount || 0) > 0 && !!basket?.discount && basket.vouchers && (
                    <>
                        <Discount>
                            <Discounts>
                                <DiscountLabel>{` - ${translate(
                                    'checkout.orderSummary.voucherLabel'
                                )}: `}</DiscountLabel>
                            </Discounts>
                        </Discount>
                        <Price discount>{freeShipping ? formatPrice(0) : formatPrice(basket.shippingDiscount)}</Price>
                    </>
                )}
            </TopSummary>
            <Grid flow="row" rowGap="extrasmall" columns="1fr 1fr">
                <PriceLabel>{`${translate('checkout.orderSummary.totalPriceInclVATLabel')} ${translate(
                    'checkout.orderSummary.inclVATLabel'
                )}`}</PriceLabel>
                <Price bold>
                    {formatPrice(
                        shipping
                            ? (basket?.totalIncVAT || 0) + (shipping?.calculatedShippingPrice || 0)
                            : basket?.totalIncVAT
                    )}
                </Price>
                {!!basket?.discount && basket.discount - (basket.voucherDiscount || 0) > 0 && (
                    <>
                        <Discount>{translate('checkout.orderSummary.yourDiscountLabel')}</Discount>
                        <Price discount>{formatPrice(basket.discount - (basket.voucherDiscount || 0))}</Price>
                    </>
                )}
            </Grid>
            {!!basket?.vouchers?.length && (
                <DiscountListContainer>
                    <Discounts>
                        <DiscountLabel>{`${translate('checkout.voucher.voucherLabel')}`}</DiscountLabel>
                        {basket?.vouchers?.map((voucher, _) => (
                            <DiscountButton
                                onClick={() => clearVoucher(voucher)}
                                type="button"
                                variant="inverted"
                                size="sm"
                                key={voucher}
                            >
                                X {voucher}
                            </DiscountButton>
                        ))}
                    </Discounts>
                </DiscountListContainer>
            )}
        </>
    );
};

function calculateShouldDisplayDefaultShipping(basket: ICalculatedBasket | undefined) {
    if (
        basket?.lines?.some((line) => line.b2CDeliveryMethod === B2CDeliveryMethod.DirectOnly) &&
        basket?.lines?.some((line) => line.b2CDeliveryMethod === B2CDeliveryMethod.ShopOnly)
    ) {
        return false;
    } else
        return (
            basket?.lines?.filter(
                (line) =>
                    (line.b2CDeliveryMethod === B2CDeliveryMethod.DirectOnly ||
                        line.b2CDeliveryMethod === B2CDeliveryMethod.ShopOnly) &&
                    !line.isGiftShopItem
            ).length === basket?.lines?.length
        );
}

function calculateTotalShippingCost(
    basket: ICalculatedBasket | undefined,
    shipping: IShippingType | undefined,
    freeShipping: boolean
) {
    if (basket?.shippingDiscount === 0) {
        return formatPrice(0 + (shipping?.calculatedShippingPrice || 0));
    } else if ((basket?.shippingDiscount || 0) > 0) {
        return freeShipping
            ? formatPrice(0)
            : formatPrice(0 + (shipping?.calculatedShippingPrice || 0) + (basket?.shippingDiscount || 0));
    } else {
        return formatPrice(0);
    }
}

const TopSummary = styled(Grid)(({ theme }) => ({
    paddingBottom: theme.space[2],
    marginBottom: theme.space[3],
    borderBottom: theme.general.border,
    rowGap: theme.space[2],
}));

const PriceLabel = styled.div(({ theme }) => ({
    fontWeight: theme.fontWeights.bold,
}));

const Price = styled.div<{ discount?: boolean; bold?: boolean }>(({ theme, discount, bold }) => ({
    justifySelf: 'flex-end',
    color: discount ? theme.colors.primaryTheme : theme.colors.black,
    fontWeight: bold ? theme.fontWeights.bold : theme.fontWeights.regular,
}));

const Discount = styled.div(({ theme }) => ({
    color: theme.colors.primaryTheme,
}));

const DiscountListContainer = styled(Flex)(({ theme }) => ({
    color: theme.colors.primaryTheme,
    marginTop: theme.space[2],
}));

const DiscountButton = styled(Button)(({ theme }) => ({
    color: theme.colors.primaryTheme,
    padding: 0,
    margin: 0,
}));

const Discounts = styled.div(({ theme }) => ({
    display: 'flex',
    gap: theme.space[2],
    flexWrap: 'wrap',
}));

const DiscountLabel = styled.span(() => ({
    flexWrap: 'nowrap',
    flexShrink: 0,
}));
